import { api, adminApi, parseApiError } from './api';
import type {
  MagicLinkRequest,
  MagicLinkResponse,
  VerifyTokenRequest,
  VerifyTokenResponse,
  AdminAuthResponse,
  User,
} from '@/types/user';

export const authService = {
  /**
   * Request a magic link to be sent via WhatsApp
   */
  async requestMagicLink(data: MagicLinkRequest): Promise<MagicLinkResponse> {
    try {
      const response = await api.post<MagicLinkResponse>('/auth/magic-link', data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Verify magic link token and get access token
   */
  async verifyToken(data: VerifyTokenRequest): Promise<VerifyTokenResponse> {
    try {
      const response = await api.post<VerifyTokenResponse>('/auth/verify', data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    try {
      const response = await api.get<User>('/auth/me');
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    try {
      const response = await api.patch<User>('/auth/me', data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Logout - invalidate token on server
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout - we'll clear local storage anyway
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Validate admin token
   */
  async validateAdminToken(token: string): Promise<AdminAuthResponse> {
    try {
      const response = await adminApi.post<AdminAuthResponse>(
        '/admin/auth/validate',
        {},
        {
          headers: { 'X-Admin-Token': token },
        }
      );
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Admin logout
   */
  adminLogout(): void {
    localStorage.removeItem('admin_token');
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  },

  /**
   * Check if admin is authenticated
   */
  isAdminAuthenticated(): boolean {
    return !!localStorage.getItem('admin_token');
  },

  /**
   * Get stored auth token
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  },

  /**
   * Get stored admin token
   */
  getAdminToken(): string | null {
    return localStorage.getItem('admin_token');
  },

  /**
   * Store auth credentials
   */
  setCredentials(token: string, user: User): void {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
  },

  /**
   * Store admin token
   */
  setAdminToken(token: string): void {
    localStorage.setItem('admin_token', token);
  },

  /**
   * Get stored user
   */
  getStoredUser(): User | null {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      try {
        return JSON.parse(userJson) as User;
      } catch {
        return null;
      }
    }
    return null;
  },
};

export default authService;
